import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { useLazySearchSyncCentersQuery } from '@dashboard/api/syncMonitor';
import DeliveryModeBadge from '@dashboard/components/SyncMonitor/DeliveryModeBadge';
import TagBadge from '@dashboard/components/SyncMonitor/TagBadge';

const DEBOUNCE_MS = 300;

const formatOptionLabel = (opt, { context }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <span>{opt.label}</span>
    {opt.deliveryMode ? (
      <DeliveryModeBadge mode={opt.deliveryMode} size="xs" />
    ) : null}
    {/* Tags only in the dropdown: the selected chip is too narrow for them. */}
    {context === 'menu' &&
      (opt.tags || []).map((tag) => <TagBadge key={tag} tag={tag} />)}
  </span>
);

export default function CenterSearchSelect({ value, onChange }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const [triggerSearch] = useLazySearchSyncCentersQuery();
  const timerRef = useRef(null);

  const loadOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        clearTimeout(timerRef.current);
        if (!inputValue || inputValue.length < 2) {
          resolve([]);
          return;
        }
        timerRef.current = setTimeout(async () => {
          try {
            const result = await triggerSearch({ q: inputValue }).unwrap();
            resolve(
              (result.data || []).map((c) => ({
                value: c.uuid,
                label: `${c.name} · ${c.external_id}`,
                externalId: c.external_id,
                deliveryMode: c.delivery_mode,
                tags: c.tags,
              })),
            );
          } catch {
            resolve([]);
          }
        }, DEBOUNCE_MS);
      }),
    [triggerSearch],
  );

  return (
    <AsyncSelect
      isMulti
      isClearable={false}
      cacheOptions
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder={t('searchPlaceholder')}
      noOptionsMessage={({ inputValue }) =>
        inputValue && inputValue.length >= 2 ? t('noResults') : null
      }
      formatOptionLabel={formatOptionLabel}
      components={{
        DropdownIndicator: null,
        IndicatorSeparator: null,
      }}
      styles={{
        container: (base) => ({ ...base, minWidth: 320, maxWidth: 600 }),
        control: (base) => ({ ...base, borderColor: '#acacac' }),
        // Options carry long names plus badges: let the menu grow past the search box.
        menu: (base) => ({
          ...base,
          width: 'max-content',
          minWidth: '100%',
          maxWidth: 720,
        }),
      }}
    />
  );
}

CenterSearchSelect.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};
